import { Col, Form, Row } from 'antd';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import CheckboxComponent from 'components/atoms/CheckboxComponent';
import InputComponent from 'components/atoms/InputComponent';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import FormWrapper from 'components/molecules/FormWrapper';

import {
    ECrsStav,
    ICrsDotaznik,
    ICrsSpolecnost,
    crsDotaznikZalozit,
    crsSpolecnostGet,
    crsSpolecnostSend
} from 'services/poDlt/crs';

import { EAnoNe } from 'enums/anoNe';

import anoNe from 'data/codebooks/anoNeSelect';

import { dicValidation } from 'utils/validations/dicValidation';
import pscValidation from 'utils/validations/pscValidation';

interface ICrsMajitel {
    nextStep: () => void;
    zadostID?: number;
    setZadostID: React.Dispatch<React.SetStateAction<number>>;
    dotaznikData: ICrsDotaznik;
}

const CrsMajitel: React.FC<ICrsMajitel> = ({ nextStep, zadostID, setZadostID, dotaznikData }) => {
    const { cisStaty, aktivniFirma } = useContext(Context);
    const [form] = Form.useForm();
    const setRerender = useState<boolean>(false)[1];
    const [adressAlt, setAddressAlt] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [spolecnostData, setSpolecnostData] = useState<ICrsSpolecnost>();
    const viewOnly = dotaznikData?.stav === ECrsStav.ZPRAC;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (value: any) => {
        setLoading(true);
        const finishData: ICrsSpolecnost = {
            ...value,
            dphplatce: value.dphplatce === EAnoNe.ANO,
            spolstaty: value.spolstaty.map((e: string) => {
                return {
                    stat: e
                };
            })
        };
        if (value.dphplatce === EAnoNe.NE) {
            finishData.dphdic && delete finishData.dphdic;
        }
        const putData = (id: number) =>
            crsSpolecnostSend(finishData, id)
                .then(() => nextStep())
                .finally(() => setLoading(false));
        if (zadostID) {
            putData(zadostID);
        } else {
            crsDotaznikZalozit()
                .then(resp => {
                    putData(resp.id);
                    setZadostID(resp.id);
                })
                .catch(() => setLoading(false));
        }
    };

    useEffect(() => {
        if (!zadostID) return;
        setLoading(true);
        crsSpolecnostGet(zadostID)
            .then(response => {
                const formatedResp = {
                    ...response,
                    spolstaty: response.spolstaty.map(e => e.stat)
                };
                setAddressAlt(response.madoruc);
                setSpolecnostData(response);
                form.setFieldsValue({
                    ...formatedResp,
                    dphplatce:
                        typeof formatedResp?.dphplatce === 'boolean'
                            ? formatedResp.dphplatce
                                ? EAnoNe.ANO
                                : EAnoNe.NE
                            : undefined
                });
            })
            .finally(() => setLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const rerender = () => {
        setRerender(val => !val);
    };

    return (
        <LoadingWrapper loading={loading}>
            <FormWrapper outForm={form} onFinish={onFinish} preserve={false}>
                <InputComponent
                    disabled={true}
                    label="IDENTIFIKACNI_CISLO"
                    name="idmb"
                    type="text"
                    maxLength={10}
                    initialValue={spolecnostData?.idmb || aktivniFirma?.ic}
                />
                <InputComponent
                    disabled={true}
                    label="ISIN_IDENTIFIKACE_FIRMA_LEGALENTITY"
                    name="nazev"
                    type="text"
                    initialValue={spolecnostData?.nazev || aktivniFirma?.spolecnost}
                    maxLength={500}
                />
                <SelectComponent
                    name="dphplatce"
                    labelParagraph="ISIN_IDENTIFIKACE_PLATCE"
                    isFormItem={true}
                    data={anoNe}
                    onChange={() => {
                        form.resetFields(['dphdic']);
                        rerender();
                    }}
                    disabled={viewOnly}
                />
                {form.getFieldValue('dphplatce') === EAnoNe.ANO && (
                    <InputComponent
                        label="ISIN_IDENTIFIKACE_DIC"
                        name="dphdic"
                        maxLength={14}
                        rules={dicValidation()}
                        disabled={viewOnly}
                    />
                )}
                <SelectComponent
                    name="spolstaty"
                    isFormItem={true}
                    labelParagraph={'FORMULARE_PROVOZOVNA_STATY_LEGALENTITY'}
                    mode="multiple"
                    data={cisStaty}
                    maxCount={3}
                    onChange={val => {
                        setRerender(pv => !pv);
                        !val.includes(aktivniFirma?.stat) &&
                            form.setFieldsValue({
                                stat: [aktivniFirma?.stat, ...val]
                            });
                    }}
                    disabled={viewOnly}
                />
                <Title level={4} text="ISIN_IDENTIFIKACE_ADRESA_LEGALENTITY" dividerTitle={true} />
                <InputComponent
                    label="ULICE_CP"
                    name="adma"
                    type="text"
                    maxLength={50}
                    initialValue={spolecnostData?.adma}
                    disabled={viewOnly}
                />
                <InputComponent
                    label="MESTO"
                    name="mema"
                    type="text"
                    maxLength={50}
                    initialValue={spolecnostData?.mema}
                    disabled={viewOnly}
                />
                <InputComponent
                    label="PSC"
                    name="pscm"
                    maxLength={10}
                    type="text"
                    rules={pscValidation('kost')}
                    initialValue={spolecnostData?.pscm}
                    disabled={viewOnly}
                />
                <SelectComponent
                    name="kost"
                    labelParagraph="STAT_SIDLA"
                    isFormItem={true}
                    defaultValue={spolecnostData?.kost}
                    data={cisStaty}
                    disabled={viewOnly}
                />
                <CheckboxComponent
                    name="madoruc"
                    checkboxText={
                        <Row>
                            <Title level={4} text="ISIN_IDENTIFIKACE_ADRESA_LEGALENTITY_ALT" />
                        </Row>
                    }
                    onChange={() => setAddressAlt(pv => !pv)}
                    defaultChecked={adressAlt}
                    checked={adressAlt}
                    style={{
                        marginBottom: 4,
                        marginTop: 10
                    }}
                    notRequired={true}
                    disabled={viewOnly}
                />

                {adressAlt && (
                    <>
                        <InputComponent
                            label="ULICE_CP"
                            name="advy"
                            type="text"
                            maxLength={50}
                            initialValue={spolecnostData?.advy}
                            disabled={viewOnly}
                        />
                        <InputComponent
                            label="MESTO"
                            name="mevy"
                            type="text"
                            maxLength={50}
                            initialValue={spolecnostData?.mevy}
                            disabled={viewOnly}
                        />
                        <InputComponent
                            label="PSC"
                            name="psvy"
                            maxLength={10}
                            rules={pscValidation('ksvy')}
                            type="text"
                            initialValue={spolecnostData?.psvy}
                            disabled={viewOnly}
                        />
                        <SelectComponent
                            name="ksvy"
                            labelParagraph="STAT_SIDLA"
                            isFormItem={true}
                            defaultValue={spolecnostData?.ksvy}
                            data={cisStaty}
                            disabled={viewOnly}
                        />
                    </>
                )}
                <Row className="button-row">
                    <Col>
                        <ButtonComponent
                            buttontext="POKRACOVAT"
                            onClick={() => (viewOnly ? nextStep() : form.submit())}
                            id="crsPokracovatBtn"
                        />
                    </Col>
                </Row>
            </FormWrapper>
        </LoadingWrapper>
    );
};

export default CrsMajitel;
